import { Callout } from '../../components/Callout';

/**
 * Shared typographic building blocks for the in-app documentation sections.
 * Every docs section module composes these so the whole manual reads as one
 * coherently-styled document.
 */

export function H1({ children }: { children: React.ReactNode }) {
  return (
    <h1 className="mb-2 text-2xl font-semibold tracking-tight text-ink">
      {children}
    </h1>
  );
}

export function H2({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="mb-3 mt-8 text-lg font-semibold text-ink first:mt-0">
      {children}
    </h2>
  );
}

export function P({ children }: { children: React.ReactNode }) {
  return <p className="mb-4 leading-relaxed text-ink-muted">{children}</p>;
}

export function UL({ children }: { children: React.ReactNode }) {
  return <ul className="mb-4 space-y-1.5 pl-5 text-ink-muted">{children}</ul>;
}

export function LI({ children }: { children: React.ReactNode }) {
  return <li className="list-disc leading-relaxed">{children}</li>;
}

export function Code({ children }: { children: React.ReactNode }) {
  return (
    <code className="rounded bg-surface-2 px-1.5 py-0.5 font-mono text-sm text-signal">
      {children}
    </code>
  );
}

export function Note({ children }: { children: React.ReactNode }) {
  return (
    <Callout tone="info" className="mb-4">
      {children}
    </Callout>
  );
}

export function Warn({ children }: { children: React.ReactNode }) {
  return (
    <Callout tone="warning" className="mb-4">
      {children}
    </Callout>
  );
}

export function Divider() {
  return <hr className="my-6 border-line" />;
}

export function StepList({
  steps,
}: {
  steps: { n: number; title: string; body: React.ReactNode }[];
}) {
  return (
    <ol className="mb-4 space-y-4">
      {steps.map((s) => (
        <li key={s.n} className="flex gap-4">
          <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-ink text-xs font-bold text-white">
            {s.n}
          </span>
          <div>
            <p className="font-medium text-ink">{s.title}</p>
            <div className="mt-1 text-sm text-ink-muted">{s.body}</div>
          </div>
        </li>
      ))}
    </ol>
  );
}
